import { useNavigate } from "react-router-dom";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import { DataTable } from "../../../components/PromotionIQ/DataTable";
import { Retailer, retailers, formatPercent, formatCurrency, historicalPromotions, plannedPromotions } from "../../../data/PromotionIQ/PromotionData";
import { ChevronRight, TrendingUp, TrendingDown, BarChart3, Target, AlertTriangle } from "lucide-react";
import React from "react";

const RetailerInvestment = () => {
  const navigate = useNavigate();

  // Sort by ROI Index descending
  const rankedRetailers = [...retailers].sort((a, b) => b.roi_index - a.roi_index);
  
  // Calculate summary metrics
  const totalYtdSales = retailers.reduce((sum, r) => sum + r.ytd_sales, 0);
  const avgRoi = retailers.reduce((sum, r) => sum + r.roi_index, 0) / retailers.length;
  const highRoiCount = retailers.filter(r => r.roi_index >= 1.3).length;
  const atRiskRetailers = retailers.filter(r => r.roi_index < 1).length;

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Retailer Investment Landscape"
        question="Which retailers convert trade dollars into value?"
      />

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <InteractiveCard
          label="Total YTD Sales"
          value={formatCurrency(totalYtdSales)}
          subValue="Across all retailers"
          variant="highlight"
          linkTo="/promotioniq/summary"
          linkLabel="View summary"
        />
        <InteractiveCard
          label="Average ROI"
          value={`${avgRoi.toFixed(2)}x`}
          subValue="Portfolio performance"
          variant={avgRoi >= 1.2 ? "success" : "default"}
          linkTo="/promotioniq/learnings"
          linkLabel="See learnings"
        />
        <InteractiveCard
          label="High Performers"
          value={highRoiCount}
          subValue="ROI ≥ 1.3x"
          variant="success"
          icon={TrendingUp}
        />
        <InteractiveCard
          label="Needs Attention"
          value={atRiskRetailers}
          subValue="ROI < 1.0x"
          variant={atRiskRetailers > 0 ? "warning" : "success"}
          icon={atRiskRetailers > 0 ? AlertTriangle : Target}
          linkTo="/promotioniq/monitoring"
          linkLabel="Review"
        />
      </div>

      {/* Ranked Retailers Table */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Retailer Rankings</h3>
          <p className="text-sm text-muted-foreground">Click any row to explore retailer economics</p>
        </div>
        <DataTable<Retailer>
          data={rankedRetailers}
          onRowClick={(retailer) => navigate(`/promotioniq/retailer-economics/${retailer.retailer_id}`)}
          columns={[
            {
              key: "rank",
              header: "Rank",
              render: (_, index) => (
                <span className="text-2xl font-bold text-primary">#{(index || 0) + 1}</span>
              ),
            },
            {
              key: "name",
              header: "Retailer",
              render: (item) => (
                <div>
                  <p className="font-medium text-foreground group-hover:text-primary transition-colors">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.economic_model}</p>
                </div>
              ),
            },
            {
              key: "roi_index",
              header: "Promotion ROI Index",
              render: (item) => (
                <div className="flex items-center gap-2">
                  {item.roi_index >= 1.2 ? (
                    <TrendingUp className="h-4 w-4 text-success" />
                  ) : item.roi_index >= 1 ? (
                    <TrendingUp className="h-4 w-4 text-warning" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-destructive" />
                  )}
                  <span className={`text-xl font-bold ${item.roi_index >= 1 ? "text-success" : "text-destructive"}`}>
                    {item.roi_index.toFixed(2)}x
                  </span>
                </div>
              ),
            },
            {
              key: "incrementality_ratio",
              header: "Incrementality Ratio",
              render: (item) => (
                <div className="flex items-center gap-2">
                  <div className="h-2 w-20 bg-muted">
                    <div
                      className="h-2 bg-primary"
                      style={{ width: `${item.incrementality_ratio * 100}%` }}
                    />
                  </div>
                  <span>{formatPercent(item.incrementality_ratio)}</span>
                </div>
              ),
            },
            {
              key: "elasticity_sensitivity",
              header: "Elasticity Band",
              render: (item) => (
                <span className={`px-3 py-1 text-sm ${
                  item.elasticity_sensitivity === "High" ? "bg-success/20 text-success" :
                  item.elasticity_sensitivity === "Medium" ? "bg-warning/20 text-warning" :
                  "bg-muted text-muted-foreground"
                }`}>
                  {item.elasticity_sensitivity}
                </span>
              ),
            },
            {
              key: "execution_reliability",
              header: "Execution Reliability",
              render: (item) => (
                <div className="flex items-center gap-2">
                  <div className="h-2 w-20 bg-muted">
                    <div
                      className={`h-2 ${item.execution_reliability >= 0.9 ? "bg-success" : item.execution_reliability >= 0.8 ? "bg-warning" : "bg-destructive"}`}
                      style={{ width: `${item.execution_reliability * 100}%` }}
                    />
                  </div>
                  <span>{formatPercent(item.execution_reliability)}</span>
                </div>
              ),
            },
            {
              key: "margin_volatility",
              header: "Margin Volatility",
              render: (item) => (
                <span className={item.margin_volatility <= 0.15 ? "text-success" : "text-warning"}>
                  ±{formatPercent(item.margin_volatility)}
                </span>
              ),
            },
            {
              key: "action",
              header: "",
              render: () => (
                <button className="btn-athena py-1 px-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <span>Profile</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              ),
            },
          ]}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {rankedRetailers.slice(0, 3).map((retailer, index) => (
          <div 
            key={retailer.retailer_id}
            onClick={() => navigate(`/promotioniq/retailer-economics/${retailer.retailer_id}`)}
            className="border border-border bg-card p-5 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`px-2 py-1 text-xs font-bold ${
                index === 0 ? "bg-success/20 text-success" : 
                index === 1 ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                #{index + 1}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <h4 className="font-bold text-lg group-hover:text-primary transition-colors">{retailer.name}</h4>
            <div className="mt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">YTD Sales</span>
                <span className="font-medium">{formatCurrency(retailer.ytd_sales)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ROI Index</span>
                <span className={`font-bold ${retailer.roi_index >= 1.3 ? "text-success" : "text-warning"}`}>
                  {retailer.roi_index.toFixed(2)}x
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Incrementality</span>
                <span className="font-medium">{formatPercent(retailer.incrementality_ratio)}</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-border">
              <div className="flex gap-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/promotioniq/retailer-promotion-truth");
                  }}
                  className="flex-1 text-xs py-2 bg-muted hover:bg-muted/80 transition-colors"
                >
                  View Truth
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/promotioniq/negotiation");
                  }}
                  className="flex-1 text-xs py-2 bg-muted hover:bg-muted/80 transition-colors"
                >
                  Negotiate
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Key Insight */}
      <div 
        onClick={() => navigate("/promotioniq/baseline-diagnostics")}
        className="border border-primary/50 bg-primary/5 p-6 cursor-pointer hover:border-primary hover:bg-primary/10 transition-all group"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
            <p className="text-lg">
              This view breaks the assumption that <span className="text-primary font-bold">all retailers are equal</span>. 
              It establishes retailer-specific thinking from the first click, showing that trade dollars perform differently across your retail partners.
            </p>
          </div>
          <div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-sm">Explore Baseline Diagnostics</span>
            <ChevronRight className="h-5 w-5" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerInvestment;
